import React from 'react';
import { Award, BookOpen, Users, Heart } from 'lucide-react';
import SEO from '../components/SEO';
import { breadcrumbSchema } from '../utils/schemaData';
import { lachlanPerson } from '../utils/structuredData';
import CrisisSupport from '../components/CrisisSupport';
import ScrollIndicator from '../components/ScrollIndicator';
import './About.css';

const About = () => {
  // Breadcrumb Schema
  const breadcrumbs = breadcrumbSchema([
    { name: "Home", url: "https://www.clinicalminds.net/" },
    { name: "About", url: "https://www.clinicalminds.net/about" }
  ]);

  const values = [
    {
      icon: <Heart size={28} strokeWidth={1.5} />,
      title: 'Genuine Connection & Compassion',
      description: 'I believe that growth will be driven by my relationship with you. I approach every client with empathy, understanding, and genuine care, knowing that the connection between us is a powerful catalyst for change. My office is a non-judgmental zone where you can be completely yourself.'
    },
    {
      icon: <Users size={28} strokeWidth={1.5} />,
      title: 'Purposeful & Client-Centred',
      description: 'This is your journey, and I honour that. I commit to working at your pace and customising my approach to your goals, focusing on what matters most in your life. My practice is built on supporting your unique path.'
    },
    {
      icon: <Award size={28} strokeWidth={1.5} />,
      title: 'Evidence-Based Practice',
      description: 'I am committed to careful, ethical psychological care. My methods are practical and grounded in current psychological research. I ensure the approaches I use are backed by science, but I always tailor them to fit who you are as a person.'
    },
    {
      icon: <BookOpen size={28} strokeWidth={1.5} />,
      title: 'Safety & Inclusivity',
      description: 'I strive to create a universally safe and welcoming space. I am dedicated to providing support with integrity and inclusivity, welcoming and affirming clients from all backgrounds and walks of life.'
    }
  ];

  return (
    <div className="page-about">
      <SEO 
        title="Lachlan Beaton | Clinical Psychologist (Registrar) | Coffs Harbour"
        description="Lachlan Beaton, Clinical Psychologist (Registrar). Autism, ADHD, FASD and learning assessments for children, teens and adults. Coffs Harbour, visiting clinics and telehealth."
        canonicalUrl="https://www.clinicalminds.net/about"
        schemaData={[lachlanPerson, breadcrumbs]}
      />
      {/* Scroll Indicator for Mobile */}
      <ScrollIndicator />
      
      {/* Hero Section */}
      <section id="main-content" className="interior-hero" data-testid="page-hero">
        <div className="container">
          <span className="section-label">About</span>
          <h1 className="heading-hero">Lachlan Beaton</h1>
          <p className="hero-subline" data-testid="about-intro">
            I'm Lachlan Beaton, a Clinical Psychologist (Registrar) based in Coffs Harbour. Most of my week is spent on neurodevelopmental assessment for children, teens and adults, covering autism, ADHD, FASD, learning and intellectual disability, done personally from the first conversation to the final report. I see people at my Coffs Harbour practice, at regular visiting clinics in Port Macquarie, Sydney, Melbourne, the Gold Coast and Hamilton, and by telehealth Australia-wide. Alongside assessment, I offer therapy with a focus on neurodiversity, teens and addiction.
          </p>
        </div>
      </section>

      <section className="about-story">
        <div className="container">
          <div className="story-layout">
            <div className="story-image">
              <img 
                src="/images/photos/lachlan-headshot.webp" 
                alt="Lachlan Beaton, Clinical Psychologist (Registrar), at Clinical Minds Psychology"
                className="about-photo"
                loading="lazy"
              />
            </div>
            <div className="story-content">
              <h2 className="heading-1">My Story</h2>
              <p className="body-medium">
                I grew up in the far west of country Victoria, on a farm with my parents and three brothers. Sport was my life when I was young, and being healthy and fit still dominates my life now. As a young adult, I studied at The University of Melbourne and then worked in Melbourne and Sydney, before deciding to call the mid-north coast of NSW, home.
              </p>
              <p className="body-medium">
                Having worked in finance and media when I was younger, I was drawn to psychology by my fascination with how people behave, and a desire to share with others.
              </p>
              <p className="body-medium">
                I became a psychologist to give people the support I wanted when I was younger. As someone who was born in rural Australia, I am passionate about providing support to those who aren't always offered it and to those who, like me, come from the regions.
              </p>
              <p className="body-medium">
                For me, therapy isn't about having all the answers. It's about creating a space where you feel heard, understood, and empowered to find your own path forward.
              </p>
              <p className="body-medium">
                When I'm not in session, you'll probably find me exploring the mid-north coast or on the beach and I love to travel and explore. My genuine curiosity and interest in others is what defines me in my psychological practice.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="about-values">
        <div className="container">
          <h2 className="heading-1">My Core Values & Approach</h2>
          <p className="body-large section-subtitle">
            I am dedicated to providing ethical, science backed, and personal psychological care. Every aspect of my practice is guided by these core principles
          </p>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card card">
                <div className="value-icon">{value.icon}</div>
                <h3 className="heading-3">{value.title}</h3>
                <p className="body-medium">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-team">
        <div className="container">
          <h2 className="heading-1">Education and Training</h2>
          <div className="team-member">
            <div className="card team-card">
              <div className="team-header">
                <h3 className="heading-2">Lachlan Beaton</h3>
                <p className="body-small team-role">Clinical Psychologist (Registrar)</p>
              </div>
              <div className="team-layout">
                <div className="team-image">
                  <img 
                    src="/images/photos/lachlan-portrait.jpg" 
                    alt="Lachlan Beaton, Clinical Psychologist (Registrar) at Clinical Minds Psychology"
                    className="team-photo"
                    loading="lazy"
                  />
                </div>
                <div className="team-content">
                
                <div className="qualifications-section">
                  <h4 className="heading-3">Key Qualifications</h4>
                  <div className="qualifications-highlights">
                    <div className="qualification-highlight">
                      <Award size={24} strokeWidth={1.5} className="qual-icon" />
                      <div>
                        <div className="qual-title">Masters of Psychology (Clinical), Bachelor of Psychological Science (Honours)</div>
                        <div className="qual-institution">University of New England, Australian Catholic University</div>
                      </div>
                    </div>
                    <div className="qualification-highlight">
                      <Award size={24} strokeWidth={1.5} className="qual-icon" />
                      <div>
                        <div className="qual-title">Bachelor of Commerce, Diploma of Arts (Political Science), CPA</div>
                        <div className="qual-institution">The University of Melbourne</div>
                      </div>
                    </div>
                    <div className="qualification-highlight">
                      <Award size={24} strokeWidth={1.5} className="qual-icon" />
                      <div>
                        <div className="qual-title">AHPRA Registered Psychologist</div>
                        <div className="qual-institution">PSY0002785698</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="therapy-style-section">
                  <h4 className="heading-3">Therapy Style</h4>
                  <p className="body-medium">I draw from a range of therapeutic approaches, tailored to your unique needs:</p>
                  <ul className="therapy-list">
                    <li className="body-medium">Schema Therapy</li>
                    <li className="body-medium">Dialectical Behaviour Therapy (DBT)</li>
                    <li className="body-medium">Person Centred Therapy</li>
                    <li className="body-medium">Attachment Based Therapy</li>
                    <li className="body-medium">Cognitive Behavioural Therapy (CBT)</li>
                    <li className="body-medium">Motivational Interviewing</li>
                    <li className="body-medium">Acceptance and Commitment Therapy</li>
                    <li className="body-medium">Trauma Informed CBT</li>
                  </ul>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-publications" id="publications">
        <div className="container">
          <span className="section-label">Research</span>
          <h2 className="heading-1">Research and Publications</h2>
          <p className="body-medium" style={{ maxWidth: '720px', marginBottom: '2rem' }}>
            Alongside clinical practice, Lachlan contributes to peer-reviewed research. His published work includes research on substance use in LGBTQIA+ communities and on the experience of caring for a loved one through a mental health crisis, work that informs his approach to therapy for addiction and his understanding of what families carry.
          </p>
          <ul className="publications-list">
            <li className="body-medium">
              <a href="https://doi.org/10.1080/00918369.2025.2540370" target="_blank" rel="noopener noreferrer" className="publication-link">
                Beaton, L., &amp; Modesto, O. (2025). Fun with Friends? Participation in the Gay Community Predicts Alcohol and Drug Consumption for Gay and Bisexual Men. <em>Journal of Homosexuality</em>. (Open access)
              </a>
            </li>
            <li className="body-medium">
              <a href="https://doi.org/10.1002/capr.70114" target="_blank" rel="noopener noreferrer" className="publication-link">
                Modesto, O., Beaton, L., Nicholson, M., Brown, C., &amp; Short, B. (2026). &ldquo;My World Is Gone&rdquo;: Understanding and Addressing the Needs of Caregivers of First Episode Psychosis Patients. <em>Counselling and Psychotherapy Research</em>.
              </a>
            </li>
          </ul>
        </div>
      </section>

      {/* Crisis Support Section */}
      <CrisisSupport />
    </div>
  );
};

export default About;
